import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Code2, Eye, FileCheck2, TrendingUp, Trophy } from "lucide-react";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

const BUCKET_LABELS = {
  top: { label: "85–100", tone: "bg-emerald-600" },
  high: { label: "70–84", tone: "bg-blue-600" },
  mid: { label: "50–69", tone: "bg-amber-500" },
  low: { label: "< 50", tone: "bg-slate-400" },
};

export default function CareersAnalyticsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/dashboard/careers-analytics?days=${days}`)
      .then(({ data }) => !cancelled && setData(data))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  // Hide the widget entirely if there's no embed activity yet.
  if (!loading && data && !data.list_views && !data.job_views && !data.applications) {
    return null;
  }

  const buckets = data?.score_buckets || { top: 0, high: 0, mid: 0, low: 0 };
  const totalScored =
    (buckets.top || 0) + (buckets.high || 0) + (buckets.mid || 0) + (buckets.low || 0);

  return (
    <div
      data-testid="careers-analytics-widget"
      className="mt-6 border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
          <Code2 className="h-3 w-3" /> Careers embed activity
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              data-testid={`careers-analytics-range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
                days === r.value
                  ? "border-blue-700 bg-blue-700 text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-px bg-slate-100 sm:grid-cols-4">
        <Stat
          label="Page views"
          value={data?.list_views ?? 0}
          icon={<Eye className="h-3 w-3" />}
          tone="text-slate-900"
          testId="careers-analytics-list-views"
        />
        <Stat
          label="Role views"
          value={data?.job_views ?? 0}
          icon={<Eye className="h-3 w-3" />}
          tone="text-slate-900"
          testId="careers-analytics-job-views"
        />
        <Stat
          label="Applications"
          value={data?.applications ?? 0}
          icon={<FileCheck2 className="h-3 w-3" />}
          tone="text-blue-700"
          testId="careers-analytics-applications"
        />
        <Stat
          label="Conversion"
          value={`${data?.conversion_pct ?? 0}%`}
          icon={<TrendingUp className="h-3 w-3" />}
          tone="text-emerald-700"
          testId="careers-analytics-conversion"
          subtitle="role view → apply"
        />
      </div>

      <div className="grid gap-px bg-slate-100 md:grid-cols-2">
        {/* Score distribution */}
        <div className="bg-white p-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            AI score distribution
          </div>
          {totalScored === 0 ? (
            <div className="mt-4 text-xs text-slate-500">
              No scored applications in this window yet.
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {(["top", "high", "mid", "low"]).map((k) => {
                const v = buckets[k] || 0;
                const pct = Math.round((v / Math.max(1, totalScored)) * 100);
                return (
                  <div
                    key={k}
                    data-testid={`careers-analytics-bucket-${k}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-12 text-[11px] font-medium tabular-nums text-slate-600">
                      {BUCKET_LABELS[k].label}
                    </div>
                    <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full ${BUCKET_LABELS[k].tone}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-12 text-right text-xs font-semibold tabular-nums text-black">
                      {v}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top jobs */}
        <div className="bg-white p-5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Top roles by applications
            </div>
            <Trophy className="h-3 w-3 text-amber-500" />
          </div>
          {data?.top_jobs?.length ? (
            <ul className="mt-4 divide-y divide-slate-100">
              {data.top_jobs.map((j, i) => (
                <li
                  key={j.id}
                  data-testid={`careers-analytics-top-job-${j.id}`}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="w-5 text-center text-[11px] font-bold text-slate-400 tabular-nums">
                      {i + 1}
                    </span>
                    <Link
                      to={`/jobs/${j.id}`}
                      className="truncate text-sm font-medium text-black hover:underline"
                    >
                      {j.title}
                    </Link>
                  </div>
                  <div className="text-xs font-semibold text-blue-700 tabular-nums">
                    {j.applications}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-4 text-xs text-slate-500">
              No applications in this window yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, tone, testId, subtitle }) {
  return (
    <div className="bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        {icon}
        {label}
      </div>
      <div
        data-testid={testId}
        className={`kpi-stat mt-2 font-heading text-2xl font-semibold tabular-nums ${tone}`}
      >
        {value}
      </div>
      {subtitle && <div className="mt-1 text-[11px] text-slate-500">{subtitle}</div>}
    </div>
  );
}
